import type { EvidenceTimelineItem } from "@/components/coach/sessionPrep/EvidenceTimelineSection";
import type {
  ArtifactItem,
  CoachLensContext,
  PracticeFocusSuggestionItem,
  PracticeSummaryItem,
  ReflectionItem,
  TranscriptItem,
} from "@/hooks/useCoachSessionPrepData";
import { buildCoachLeaderEvidenceSummary } from "@/lib/behavior/viewModels/buildCoachLeaderEvidenceSummary";
import { isTranscriptNeedsCoachReview } from "@/lib/transcriptStatus";
import type { CoachEvidenceCanonicalIdentity } from "@/lib/coachEvidenceResolver";

function firstSentence(value?: string | null) {
  if (!value) return "";
  const normalized = value.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  const match = normalized.match(/(.+?[.!?])(\s|$)/);
  return match?.[1]?.trim() || normalized;
}

function trimSingleLine(value: string, max = 220) {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1)}...`;
}

function buildCoachLensSummary(params: {
  transcriptSummary: string | null;
  practiceSummary: PracticeSummaryItem | null;
  coachLens: CoachLensContext | null;
}) {
  const observed = firstSentence(params.practiceSummary?.behavior_insights)
    || firstSentence(params.practiceSummary?.transcript_summary)
    || firstSentence(params.transcriptSummary)
    || "Transcript uploaded and analyzed.";
  const lens = firstSentence(params.coachLens?.coaching_philosophy)
    || firstSentence(params.coachLens?.voice_summary);
  const prompt = firstSentence(params.practiceSummary?.coaching_prompt);

  return trimSingleLine([
    `Observed: ${observed}`,
    lens ? `Coaching lens: ${lens}` : "",
    prompt ? `Prep question: ${prompt}` : "",
  ].filter(Boolean).join(" "));
}

function sourceTypeLabel(value: string) {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function buildPracticeFocusDetail(item: PracticeFocusSuggestionItem) {
  const evidenceSummary = item.evidence
    .map((entry) => entry.leader_safe_summary || `${sourceTypeLabel(entry.evidence_role)} evidence from ${sourceTypeLabel(entry.source_type)}.`)
    .find(Boolean);
  const response = item.leader_response
    ? `Leader response: ${item.leader_response.replace(/_/g, " ")}.`
    : "";

  return trimSingleLine([
    `Practice prompt: ${item.practice_prompt}`,
    item.rationale_summary ? `Why now: ${item.rationale_summary}` : "",
    evidenceSummary ? `Evidence: ${evidenceSummary}` : "",
    response,
  ].filter(Boolean).join(" "));
}

export type CoachSessionPrepEvidenceViewModel = {
  evidenceItems: Array<EvidenceTimelineItem & { subtype: string | null; meeting_id: string | null; needsReview?: boolean; canonicalIdentity: CoachEvidenceCanonicalIdentity }>;
  latestEvidence: (EvidenceTimelineItem & { subtype: string | null; meeting_id: string | null; needsReview?: boolean; canonicalIdentity: CoachEvidenceCanonicalIdentity }) | null;
  sectionNewCounts: ReturnType<typeof buildCoachLeaderEvidenceSummary>["newCounts"];
  snapshot: ReturnType<typeof buildCoachLeaderEvidenceSummary>["snapshot"];
};

export function buildCoachSessionPrepEvidence(params: {
  leaderId?: string;
  transcripts: TranscriptItem[];
  reflections: ReflectionItem[];
  artifacts: ArtifactItem[];
  practiceSummaries: PracticeSummaryItem[];
  practiceFocusSuggestions: PracticeFocusSuggestionItem[];
  coachLens: CoachLensContext | null;
  lastSeenAt: string | null;
  recentWindowDays: number;
}): CoachSessionPrepEvidenceViewModel {
  const leaderId = params.leaderId || "";
  const practiceByTranscriptUpload = new Map<string, PracticeSummaryItem>();
  const practiceByMeeting = new Map<string, PracticeSummaryItem>();
  params.practiceSummaries.forEach((item) => {
    if (item.transcript_upload_id && !practiceByTranscriptUpload.has(item.transcript_upload_id)) {
      practiceByTranscriptUpload.set(item.transcript_upload_id, item);
    }
    if (item.meeting_id && !practiceByMeeting.has(item.meeting_id)) {
      practiceByMeeting.set(item.meeting_id, item);
    }
  });

  const seenTranscriptIds = new Set<string>();
  const fromTranscripts = params.transcripts
    .filter((item) => {
      const key = item.id;
      if (seenTranscriptIds.has(key)) return false;
      seenTranscriptIds.add(key);
      return true;
    })
    .map((item) => {
      const matchedPracticeSummary = practiceByTranscriptUpload.get(item.id)
        || (item.meeting_id ? practiceByMeeting.get(item.meeting_id) : null)
        || null;
      return {
        id: `transcript-${item.id}`,
        created_at: item.created_at,
        title: item.title,
        detail: buildCoachLensSummary({
          transcriptSummary: item.summary,
          practiceSummary: matchedPracticeSummary,
          coachLens: params.coachLens,
        }),
        link: item.link,
        source: "Transcript" as const,
        subtype: "transcript",
        meeting_id: item.meeting_id,
        needsReview: isTranscriptNeedsCoachReview(item.analysis_status, item.coach_verified),
        canonicalIdentity: {
          sourceType: "transcript_upload" as const,
          sourceId: item.id,
          leaderId: item.leader_id || leaderId,
        },
      };
    });

  const fromReflections = params.reflections
    .filter((item) => !!item.created_at)
    .map((item) => ({
      id: `reflection-${item.id}`,
      created_at: item.created_at || "",
      title: item.title,
      detail: item.text || "Reflection submitted.",
      link: item.link,
      source: "Reflection" as const,
      subtype: item.source === "shared_reflection" ? "shared-reflection" : "reflection",
      meeting_id: item.meeting_id,
      canonicalIdentity: {
        sourceType: item.source === "assessment_reflection"
          ? "assessment" as const
          : item.source === "shared_reflection"
            ? "shared_reflection" as const
            : "post_meeting_reflection" as const,
        sourceId: item.canonical_source_id || item.id,
        leaderId: item.leader_id || leaderId,
      },
    }));

  const fromArtifacts = params.artifacts.map((item) => ({
    id: `artifact-${item.id}`,
    created_at: item.created_at,
    title: item.title,
    detail: item.type,
    link: item.link,
    source: "Artifact" as const,
    subtype: "artifact",
    meeting_id: null,
    canonicalIdentity: {
      sourceType: "leader_artifact" as const,
      sourceId: item.id,
      leaderId: item.leader_id || leaderId,
    },
  }));

  const fromPracticeFocusSuggestions = params.practiceFocusSuggestions
    .filter((item) => item.visibility_scope === "coach_reviewed" && item.ledger_visibility_scope === "coach_only")
    .map((item) => ({
      id: `practice-focus-${item.id}`,
      created_at: item.last_observed_at || item.created_at || "",
      title: `Practice Focus: ${item.title}`,
      detail: buildPracticeFocusDetail(item),
      link: params.leaderId ? `/coach/leaders/${params.leaderId}?section=practice-focus-review` : null,
      source: "Practice Focus" as const,
      subtype: "practice-focus",
      meeting_id: null,
      needsReview: true,
      canonicalIdentity: {
        sourceType: "practice_focus_suggestion" as const,
        sourceId: item.id,
        leaderId: item.leader_id || leaderId,
      },
    }));

  const evidenceItems = [...fromTranscripts, ...fromReflections, ...fromArtifacts, ...fromPracticeFocusSuggestions]
    .filter((item) => !!item.created_at)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const latestEvidence = evidenceItems[0] || null;
  const windowStartTs = Date.now() - params.recentWindowDays * 24 * 60 * 60 * 1000;
  const inWindow = (value: string | null | undefined) => {
    if (!value) return false;
    const ts = new Date(value).getTime();
    return !Number.isNaN(ts) && ts >= windowStartTs;
  };

  const evidenceSummary = buildCoachLeaderEvidenceSummary({
    transcriptDates: params.transcripts.map((i) => (inWindow(i.created_at) ? i.created_at : null)),
    reflectionDates: params.reflections.map((i) => (inWindow(i.created_at) ? i.created_at : null)),
    artifactDates: params.artifacts.map((i) => (inWindow(i.created_at) ? i.created_at : null)),
    lastSeenAt: params.lastSeenAt,
    latestEvidenceAt: latestEvidence?.created_at || null,
  });

  return {
    evidenceItems,
    latestEvidence,
    sectionNewCounts: evidenceSummary.newCounts,
    snapshot: evidenceSummary.snapshot,
  };
}
